/**
 * Land on the conversation an arrival link named (#1599 Story 2.3, FR-8).
 *
 * The request screen reads `thread=` ONCE from the URL (`discussionEntry.ts`)
 * and seeds the panel's session with it (`arrivalThreadId`). This hook is the
 * other half: it turns that id into an open conversation, or into nothing.
 *
 * ⚠️ RESOLVED, NEVER OPENED DIRECTLY, FOR TWO REASONS. (1) The client cannot
 * ask "may I read thread X" without listing: the messages query for an
 * unreadable id rejects (the server answers a refusal as not-found) and the
 * hook renders it as the error bar, which an arrival must never show. (2)
 * The URL carries no space, and the thread view is keyed on `openThreadId`
 * alone: a shared conversation opened while the tab says Vertiv would sit
 * under "Not visible to the customer" while every reply goes to the
 * customer. So the hook lists the spaces the viewer may see, finds the row,
 * and writes the row's SPACE and then its id, the tab press's own order.
 * An external viewer is never asked for `internal` (NFR-3): readability is
 * "the row is in a listing the viewer may read", which the server's
 * `authorizeListing` already decides per space.
 *
 * ⚠️ THE SESSION IS THE ONLY SINK. A resize mid-resolution remounts the
 * panel (docked and overlay are two mounts, never both at once), so nothing
 * of the result is mount state: it is written to the session, which outlives
 * both mounts (`spec-1069-panel-survives-resize.md`, ruling 1). For the same
 * reason there is no cancellation on unmount; only a revoke stops the write.
 *
 * ⚠️ CONSUMED FIRST. The field is cleared before the listings go out and the
 * id kept in the closure, so a remount or a re-render mid-fetch cannot start
 * a second resolution.
 *
 * The listings go through `fetchQuery` on `threadsQuery`, the panel's OWN key
 * (`correspondenceThreadsKeys.list`): the rows land where `useCorrespondence`
 * reads them, a listing already in flight for the same key is shared, and no
 * new endpoint is called. It is not free: `staleTime` is 0, so a key with no
 * request in flight is fetched again, and the panel's own query may list the
 * arrival's space once more after the space is written. A rejected listing has two answers: the rows the
 * cache still holds for that key (an offline device, `keptOffline.ts`'s
 * precedent), else none. Not found means the list stays, with no error.
 *
 * And the arrival YIELDS: if the user changed tab, opened a conversation
 * or started one while the listings were on the wire, their press is newer
 * than the link they clicked, and nothing is written.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { threadsQuery } from './correspondenceQueries';

import type { SpaceId, Thread } from './correspondence.types';
import type { CorrespondenceSession } from './correspondenceSession';
import type { IMessagingProvider } from './providers';

export interface UseArrivalThreadArgs {
  session: CorrespondenceSession;
  requestId: string;
  viewerId: string | null;
  canSeeInternal: boolean;
  provider: IMessagingProvider;
  /** The panel is showing and may ask (the hook's own `enabled`). */
  enabled: boolean;
  accessRevoked: boolean;
}

export function useArrivalThread({
  session,
  requestId,
  viewerId,
  canSeeInternal,
  provider,
  enabled,
  accessRevoked,
}: UseArrivalThreadArgs): void {
  const queryClient = useQueryClient();
  // Read at write time, so a revoke that lands mid-flight stops the write.
  const revokedRef = useRef(accessRevoked);
  revokedRef.current = accessRevoked;
  // The provider is read when the resolution starts, never a dependency: a
  // new provider object must not restart an arrival that was consumed.
  const providerRef = useRef(provider);
  providerRef.current = provider;

  useEffect(() => {
    const arrivalId = session.get().arrivalThreadId;
    if (arrivalId === null) return;
    // Not consumed: the effect re-runs when the viewer is known and the
    // panel is showing.
    if (!enabled || viewerId === null || revokedRef.current) return;
    session.set('arrivalThreadId', null);

    // The arrival YIELDS to anything the user does while the listings are
    // on the wire: a tab press (`space`), a conversation opened (even one
    // left again with Back, which puts `openThreadId` back to null), or a new
    // conversation started (`composingThread`). Watched, not compared at the
    // end, because an open-then-Back leaves no trace in the final state.
    const before = session.get();
    let userActed = false;
    const unsubscribe = session.subscribe(() => {
      const now = session.get();
      if (
        now.space !== before.space ||
        now.openThreadId !== before.openThreadId ||
        now.composingThread !== before.composingThread
      ) {
        userActed = true;
      }
    });

    const spaces: readonly SpaceId[] = canSeeInternal
      ? ['internal', 'shared']
      : ['shared'];
    const listing = (space: SpaceId) =>
      threadsQuery({
        provider: providerRef.current,
        requestId,
        space,
        viewerId,
      });

    // Each listing answers for itself: its rows, or on a rejection what the
    // cache still holds for its key, or none.
    const rowsFor = (space: SpaceId): Promise<Thread[]> =>
      queryClient
        .fetchQuery(listing(space))
        .catch(
          () =>
            queryClient.getQueryData<Thread[]>(listing(space).queryKey) ?? [],
        );

    Promise.allSettled(spaces.map(rowsFor)).then(results => {
      unsubscribe();
      if (userActed) return;
      const rows: Thread[] = results.flatMap(result =>
        result.status === 'fulfilled' ? result.value : [],
      );
      const row = rows.find(candidate => candidate.id === arrivalId);
      if (!row) return;
      if (revokedRef.current) return;
      const now = session.get();
      if (now.openThreadId !== null || now.composingThread) return;
      session.set('space', row.space);
      session.set('openThreadId', row.id);
    });
  }, [session, requestId, viewerId, canSeeInternal, enabled, queryClient]);
}
